import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import {
  Clock,
  Users,
  Briefcase,
  ChevronDown,
  ChevronRight,
  Calendar,
  Download,
  Search,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DATE_PRESETS = {
  this_month: "This Month",
  last_30: "Last 30 Days",
  last_month: "Last Month",
  last_90: "Last 90 Days",
  this_year: "This Year",
  all_time: "All Time",
  custom: "Custom Range",
};

const getDateRange = (preset) => {
  const now = new Date();
  const startOfDay = (d) => { d.setHours(0, 0, 0, 0); return d; };
  const endOfDay = (d) => { d.setHours(23, 59, 59, 999); return d; };

  switch (preset) {
    case "this_month": {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: startOfDay(start), end: endOfDay(now) };
    }
    case "last_30": {
      const start = new Date(now); start.setDate(start.getDate() - 30);
      return { start: startOfDay(start), end: endOfDay(now) };
    }
    case "last_month": {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: startOfDay(start), end: endOfDay(end) };
    }
    case "last_90": {
      const start = new Date(now); start.setDate(start.getDate() - 90);
      return { start: startOfDay(start), end: endOfDay(now) };
    }
    case "this_year": {
      const start = new Date(now.getFullYear(), 0, 1);
      return { start: startOfDay(start), end: endOfDay(now) };
    }
    case "all_time": return { start: null, end: null };
    default: return { start: null, end: null };
  }
};

const AdminConsultantHours = () => {
  const { token } = useAuth();
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState("this_month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [expandedConsultants, setExpandedConsultants] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const fetchReport = useCallback(async (startDate, endDate) => {
    setLoading(true);
    try {
      let url = `${API}/admin/consultant-hours-report`;
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate.toISOString());
      if (endDate) params.append("end_date", endDate.toISOString());
      if (params.toString()) url += `?${params}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setReport(data);
        const expanded = {};
        data.forEach((c) => { if (c.total_hours > 0) expanded[c.consultant_id] = true; });
        setExpandedConsultants(expanded);
      }
    } catch (err) {
      console.error("Request failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (datePreset === "custom") return;
    const range = getDateRange(datePreset);
    fetchReport(range.start, range.end);
  }, [datePreset, fetchReport]);

  const handleCustomSearch = () => {
    if (customStart && customEnd) fetchReport(new Date(customStart), new Date(customEnd + "T23:59:59"));
  };

  const toggleConsultant = (id) => {
    setExpandedConsultants((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredReport = useMemo(() => {
    if (!searchQuery) return report;
    const q = searchQuery.toLowerCase();
    return report.filter((c) => c.consultant_name?.toLowerCase().includes(q));
  }, [report, searchQuery]);

  const totalHours = report.reduce((sum, c) => sum + c.total_hours, 0);
  const activeConsultants = report.filter((c) => c.total_hours > 0).length;

  const exportCSV = () => {
    const rows = [["Consultant", "Project", "Client", "Hours", "Entries"]];
    report.forEach((c) => {
      if (c.projects.length === 0) rows.push([c.consultant_name, "", "", "0", "0"]);
      else c.projects.forEach((p) => rows.push([c.consultant_name, p.project_name, p.client_name, p.hours.toString(), p.entry_count.toString()]));
    });
    const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `consultant-hours-${datePreset}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout activeNav="consultant-hours">
      <div className="max-w-[1200px] mx-auto animate-fade-in" data-testid="consultant-hours-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-rnb-navy tracking-tight" data-testid="page-title">
              Consultant Hours
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Track hours logged by consultants across all projects
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCSV}
            className="text-xs gap-1.5 h-9 border-gray-200 text-gray-500 hover:text-rnb-navy"
            data-testid="export-csv-btn"
          >
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        </div>

        {/* Filters Row */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search consultant..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl text-rnb-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-rnb-blue/20 focus:border-rnb-blue/30 transition-all"
              data-testid="consultant-search"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Calendar className="w-4 h-4 text-gray-400" />
            <Select value={datePreset} onValueChange={setDatePreset}>
              <SelectTrigger className="w-44 h-10 text-sm border-gray-200 rounded-xl" data-testid="date-preset-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DATE_PRESETS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {datePreset === "custom" && (
              <>
                <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="w-40 rounded-xl" data-testid="custom-start-date" />
                <span className="text-xs text-gray-400">to</span>
                <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="w-40 rounded-xl" data-testid="custom-end-date" />
                <Button size="sm" onClick={handleCustomSearch} className="h-10 bg-rnb-navy hover:bg-rnb-navy/90" data-testid="apply-custom-date">Apply</Button>
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: "Total Hours", value: totalHours.toFixed(1), icon: Clock, bg: "bg-rnb-blue/10", color: "text-rnb-blue", testId: "total-hours-stat" },
            { label: "Active Consultants", value: activeConsultants, icon: Users, bg: "bg-rnb-teal/10", color: "text-rnb-teal", testId: "active-consultants-stat" },
            { label: "Total Consultants", value: report.length, icon: Briefcase, bg: "bg-violet-50", color: "text-violet-500", testId: "total-consultants-stat" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 px-5 py-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-[18px] h-[18px] ${s.color}`} />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{s.label}</p>
                <p className="font-accent text-xl font-bold text-rnb-navy" data-testid={s.testId}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Consultant List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-rnb-blue/20 border-t-rnb-blue rounded-full animate-spin" />
          </div>
        ) : filteredReport.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 py-16 text-center">
            <Users className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">
              {searchQuery ? "No consultants match your search" : "No consultant data for this period"}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="consultant-list">
            {filteredReport.map((consultant) => (
              <div key={consultant.consultant_id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <button
                  className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50/80 transition-colors text-left group"
                  onClick={() => toggleConsultant(consultant.consultant_id)}
                  data-testid={`consultant-row-${consultant.consultant_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-rnb-navy/90 to-rnb-blue flex items-center justify-center text-white text-xs font-semibold">
                      {consultant.consultant_name?.charAt(0) || "?"}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-rnb-navy">{consultant.consultant_name}</p>
                      <p className="text-xs text-gray-400">
                        {consultant.project_count} project{consultant.project_count !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-accent text-lg font-bold text-rnb-navy">{consultant.total_hours.toFixed(1)}h</p>
                    </div>
                    {expandedConsultants[consultant.consultant_id] ? (
                      <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-rnb-navy transition-colors" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-rnb-navy transition-colors" />
                    )}
                  </div>
                </button>

                {expandedConsultants[consultant.consultant_id] && (
                  <div className="border-t border-gray-50 bg-gray-50/30">
                    {consultant.projects.length === 0 ? (
                      <div className="px-5 py-4 text-sm text-gray-400 text-center">No hours logged in this period</div>
                    ) : (
                      <div>
                        <div className="grid grid-cols-12 gap-2 px-5 py-2.5 text-[11px] font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100">
                          <div className="col-span-5">Project</div>
                          <div className="col-span-3">Client</div>
                          <div className="col-span-2 text-right">Hours</div>
                          <div className="col-span-2 text-right">Entries</div>
                        </div>
                        {consultant.projects.map((p) => (
                          <div key={p.project_id} className="grid grid-cols-12 gap-2 px-5 py-3 text-sm items-center hover:bg-gray-50/80 transition-colors border-b border-gray-50 last:border-b-0" data-testid={`project-row-${p.project_id}`}>
                            <div className="col-span-5 font-medium text-rnb-navy truncate">{p.project_name}</div>
                            <div className="col-span-3 text-gray-500 truncate">{p.client_name || "—"}</div>
                            <div className="col-span-2 text-right">
                              <Badge className="text-[10px] px-2 py-0.5 bg-rnb-blue/10 text-rnb-blue border border-rnb-blue/20 hover:bg-rnb-blue/10 font-mono">
                                {p.hours.toFixed(1)}h
                              </Badge>
                            </div>
                            <div className="col-span-2 text-right text-gray-400 text-xs">{p.entry_count}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminConsultantHours;
